import React, { useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AccountAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';

const inputCls = 'w-full px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551] focus:ring-2 focus:ring-[#0A1551]/10';

export const ChangePassword = () => {
  const { user, checkAuth } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const mismatch = !!confirm && next !== confirm;
  const tooShort = !!next && next.length < 10;
  const canSubmit = current && next.length >= 10 && next === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await AccountAPI.changePassword(current, next);
      setCurrent(''); setNext(''); setConfirm('');
      toast.success('Password changed — please sign in again with your new password');
      await checkAuth();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not change your password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E3E5F5] rounded-xl p-5 max-w-[480px]" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-lg bg-[#F0F1FB] flex items-center justify-center"><KeyRound className="w-4 h-4 text-[#0A1551]" /></span>
        <div>
          <p className="text-[14px] font-bold text-[#0A1551]">Change your password</p>
          <p className="text-[12px] text-[#6F76A7]">Signed in as {user?.email}</p>
        </div>
      </div>
      <p className="text-[12px] text-[#6F76A7] mt-3">
        If you were given a generated password, replace it with one only you know. Changing it signs you out everywhere.
      </p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <div>
          <label htmlFor="cp-current" className="block text-[13px] font-semibold text-[#0A1551] mb-1">Current password</label>
          <input id="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password" data-testid="change-password-current" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cp-new" className="block text-[13px] font-semibold text-[#0A1551] mb-1">New password</label>
          <input id="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password" data-testid="change-password-new" className={inputCls} />
          {tooShort && <p className="mt-1 text-[12px] text-[#E5387B]" data-testid="change-password-short">Use at least 10 characters.</p>}
        </div>
        <div>
          <label htmlFor="cp-confirm" className="block text-[13px] font-semibold text-[#0A1551] mb-1">Confirm new password</label>
          <input id="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password" data-testid="change-password-confirm" className={inputCls} />
          {mismatch && <p className="mt-1 text-[12px] text-[#E5387B]" data-testid="change-password-mismatch">These passwords don&apos;t match.</p>}
        </div>
        <button type="submit" disabled={!canSubmit} data-testid="change-password-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[14px] font-semibold disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
        </button>
      </form>
    </div>
  );
};

export default ChangePassword;
